import React from 'react';
import { Link } from 'react-router-dom';
import Image from 'components/AppImage';
import Icon from 'components/AppIcon';

const Footer = () => {
  const currentYear = new Date()?.getFullYear();

  const quickLinks = [
    { label: 'Home', path: '/home-landing' },
    { label: 'Accommodations', path: '/accommodation-details' },
    { label: 'Resorts', path: '/resorts-overview' },
    { label: 'Activities', path: '/activities-experiences' },
    { label: 'Gallery', path: '/photo-gallery' },
    { label: 'Contact', path: '/contact-directions' }
  ];

  const services = [
    'Adventure Activities',
    'Luxury Accommodations', 
    'Jungle Safaris',
    'White Water Rafting',
    'Corporate Events',
    'Wedding Venues'
  ];

  const contactInfo = {
    address: 'Dandeli Forest Reserve, Karnataka 581325',
    phone: '+91 8277385225',
    email: 'info@dandeliadventureresorts.com',
    emergencyPhone: '+91 8277385226'
  };

  const socialLinks = [
    { name: 'Facebook', icon: 'Facebook', url: '#' },
    { name: 'Instagram', icon: 'Instagram', url: '#' },
    { name: 'Twitter', icon: 'Twitter', url: '#' },
    { name: 'YouTube', icon: 'Youtube', url: '#' }
  ];

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hi! I'd like to know more about Dandeli Adventure Resorts. Can you provide me with details?");
    window.open(`https://wa.me/918277385225?text=${message}`, '_blank');
  };

  const handleCallClick = () => {
    window.open(`tel:${contactInfo?.phone}`, '_self');
  };

  const handleEmailClick = () => {
    window.open(`mailto:${contactInfo?.email}`, '_self');
  };

  return (
    <footer className="bg-text-primary text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-2 mb-6">
              <Image
                src="/assets/images/Logo-1758034090926.png"
                alt="Dandeli Adventure Resorts"
                className="w-10 h-10 object-contain"
              />
              <div>
                <h3 className="text-xl font-heading font-bold text-white">
                  Dandeli Adventure
                </h3>
                <p className="text-sm text-white/80 font-caption">
                  Resorts
                </p>
              </div>
            </div>
            <p className="text-white/80 mb-6 font-body">
              Experience the perfect blend of luxury and adventure in the heart of Karnataka's pristine wilderness. Your gateway to unforgettable memories.
            </p>
            <div className="flex space-x-4">
              {socialLinks?.map((social) => (
                <a
                  key={social?.name}
                  href={social?.url}
                  className="w-10 h-10 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-organic group"
                  aria-label={social?.name}
                >
                  <Icon name={social?.icon} size={18} className="text-white group-hover:text-white" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-heading font-semibold text-white mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks?.map((link) => (
                <li key={link?.path}>
                  <Link
                    to={link?.path}
                    className="text-white/80 hover:text-white transition-organic font-body text-sm flex items-center"
                  >
                    <Icon name="ChevronRight" size={14} className="mr-2" />
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-heading font-semibold text-white mb-6">
              Our Services
            </h4>
            <ul className="space-y-3">
              {services?.map((service) => (
                <li key={service} className="text-white/80 font-body text-sm flex items-center">
                  <Icon name="Check" size={14} className="mr-2 text-primary" />
                  {service}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-heading font-semibold text-white mb-6">
              Contact Us
            </h4>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Icon name="MapPin" size={18} className="text-primary mt-1 flex-shrink-0" />
                <p className="text-white/80 font-body text-sm">
                  {contactInfo?.address}
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <Icon name="Phone" size={18} className="text-primary flex-shrink-0" />
                <button
                  onClick={handleCallClick}
                  className="text-white/80 hover:text-white transition-organic font-body text-sm"
                >
                  {contactInfo?.phone}
                </button>
              </div>
              <div className="flex items-center space-x-3">
                <Icon name="Mail" size={18} className="text-primary flex-shrink-0" />
                <button
                  onClick={handleEmailClick}
                  className="text-white/80 hover:text-white transition-organic font-body text-sm"
                >
                  {contactInfo?.email}
                </button>
              </div>
              <div className="flex items-center space-x-3">
                <Icon name="Clock" size={18} className="text-primary flex-shrink-0" />
                <p className="text-white/80 font-body text-sm">
                  24/7 Guest Support
                </p>
              </div>
            </div>

            {/* Contact Buttons */}
            <div className="mt-6 space-y-3">
              <button
                onClick={handleWhatsAppClick}
                className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-organic flex items-center justify-center font-heading font-medium"
              >
                <Icon name="MessageCircle" size={16} className="mr-2" />
                Chat on WhatsApp
              </button>
              <button
                onClick={handleCallClick}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-lg transition-organic flex items-center justify-center font-heading font-medium"
              >
                <Icon name="Phone" size={16} className="mr-2" />
                Call Now
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="text-white/80 font-body text-sm mb-4 md:mb-0">
              © {currentYear} Dandeli Adventure Resorts. All rights reserved.
            </div>
            <div className="flex flex-wrap items-center space-x-6 text-sm">
              <a href="#" className="text-white/80 hover:text-white transition-organic font-body">
                Privacy Policy
              </a>
              <a href="#" className="text-white/80 hover:text-white transition-organic font-body">
                Terms & Conditions
              </a>
              <a href="#" className="text-white/80 hover:text-white transition-organic font-body">
                Cancellation Policy
              </a>
              <a href="#" className="text-white/80 hover:text-white transition-organic font-body">
                Sitemap
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Emergency Contact Strip */}
      <div className="bg-error text-error-foreground py-2">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-center text-sm font-heading font-medium">
            <Icon name="AlertTriangle" size={16} className="mr-2" />
            24/7 Emergency Assistance: {contactInfo?.emergencyPhone}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;